<script setup>
import headerLogo from '@/assets/images/logo/header.png';

const API_BASE = 'https://api.igeotechgps.com/api-tms';

const endpoints = [
  {
    method: 'GET',
    path: '/external/free-trackings',
    title: 'List free trackings',
    summary: 'Trips whose start time falls inside the date window, newest first. Pass updatedSince instead to read only trips created or changed after that time, oldest change first. Points are not included.',
    params: [
      ['from', 'Required unless updatedSince is set', 'YYYY-MM-DD or epoch milliseconds. A date starts at 00:00:00.000 UTC.'],
      ['to', 'Required unless updatedSince is set', 'YYYY-MM-DD or epoch milliseconds. A date ends at 23:59:59.999 UTC. The window cannot be longer than 31 days.'],
      ['updatedSince', 'Optional', 'Epoch milliseconds. When set, from and to are not used. Returns trips whose updatedAt is greater than this value. Page until a page has fewer than 50 trips, then pass the greatest updatedAt you received as the next updatedSince.'],
      ['driverId', 'Optional', 'One driver in this fleet.'],
      ['page', 'Optional', 'Starts at 1. Each page has 50 trips.'],
    ],
    request: `/external/free-trackings?from=2026-10-01&to=2026-10-11&page=1`,
    response: `{
  "success": true,
  "data": {
    "items": [
      {
        "id": 42,
        "driverId": 7,
        "driverName": "Asha Kumar",
        "startedAt": 1791695220000,
        "endedAt": 1791697020000,
        "distanceM": 32900,
        "movingSeconds": 1400,
        "idleSeconds": 180,
        "stopCount": 2,
        "pointCount": 640,
        "endReason": "manual",
        "updatedAt": 1791697100000,
        "useType": "work",
        "vehicleType": "Van",
        "plateNumber": "WXY1234",
        "notes": "Morning delivery"
      }
    ],
    "page": 1,
    "pageSize": 50,
    "total": 1
  }
}`,
  },
  {
    method: 'GET',
    path: '/external/free-trackings/{id}/points',
    title: 'List points for one trip',
    summary: 'The trip must belong to the fleet that owns the key. Each page has 100 points, in recorded order.',
    params: [
      ['id', 'Required', 'Trip id from the list, in the path.'],
      ['page', 'Optional', 'Starts at 1.'],
    ],
    request: `/external/free-trackings/42/points?page=1`,
    response: `{
  "success": true,
  "data": {
    "page": 1,
    "pageSize": 100,
    "total": 640,
    "items": [
      {
        "recordedAt": 1791695220000,
        "lat": 3.139,
        "lng": 101.6869,
        "speed": 8.4,
        "heading": 87,
        "mileageM": 120,
        "address": "Jalan Ampang, Kuala Lumpur"
      }
    ]
  }
}`,
  },
  {
    method: 'GET',
    path: '/external/drivers',
    title: 'List drivers',
    summary: 'Drivers in this fleet, ordered by name. Each page has 50 drivers.',
    params: [
      ['q', 'Optional', 'Matches first name, last name, or phone.'],
      ['page', 'Optional', 'Starts at 1.'],
    ],
    request: `/external/drivers?q=Asha&page=1`,
    response: `{
  "success": true,
  "data": {
    "items": [
      {
        "id": 7,
        "firstName": "Asha",
        "lastName": "Kumar",
        "phone": "+60123456789",
        "status": 1,
        "freeTrackingEnabled": true,
        "freeTrackingAutoStart": false,
        "freeTrackingInSettings": false
      }
    ],
    "page": 1,
    "pageSize": 50,
    "total": 1
  }
}`,
  },
  {
    method: 'GET',
    path: '/external/drivers/{id}',
    title: 'Get one driver',
    summary: 'Full profile for a driver in this fleet: details, address, ID, emergency contact, photo, zones, and app sign-in info. Fields that are not filled in are null.',
    params: [
      ['id', 'Required', 'Driver id, in the path.'],
    ],
    request: `/external/drivers/7`,
    response: `{
  "success": true,
  "data": {
    "id": 7,
    "firstName": "Asha",
    "lastName": "Kumar",
    "phone": "+60123456789",
    "email": "asha@example.com",
    "licenseNumber": "D1234567",
    "licenseExpiry": "2027-04-30",
    "licenseType": "D",
    "truckType": "Van",
    "status": 1,
    "freeTrackingEnabled": true,
    "freeTrackingAutoStart": false,
    "freeTrackingInSettings": false,
    "address": {
      "street": "12 Jalan Ampang",
      "city": "Kuala Lumpur",
      "state": "WP Kuala Lumpur",
      "postalCode": "50450",
      "country": "Malaysia"
    },
    "identity": { "type": "nric", "number": "900101-14-1234" },
    "emergencyContact": { "name": "Ravi Kumar", "phone": "+60129876543", "relationship": "Brother" },
    "photoUrl": "https://api.igeotechgps.com/uploads/1792562172317_ab12c_photo.jpg",
    "zones": [{ "id": 2, "name": "Klang Valley", "color": "#28C76F" }],
    "openSessions": 1,
    "lastSignInAt": 1792562172317,
    "lastSignInDevice": { "platform": "android", "model": "21081111RG" },
    "registeredDevices": 1
  }
}`,
  },
  {
    method: 'GET',
    path: '/external/drivers/{id}/sessions',
    title: 'List driver sessions',
    summary: 'App sign-ins for a driver, newest first. open is true while the driver is still signed in on that phone. Times are epoch milliseconds.',
    params: [
      ['id', 'Required', 'Driver id, in the path.'],
      ['page', 'Optional', '1 or more. Defaults to 1.'],
      ['pageSize', 'Optional', '1 to 50. Defaults to 20.'],
      ['open', 'Optional', 'true for signed-in sessions only, false for ended sessions only.'],
    ],
    request: '/external/drivers/7/sessions?open=true',
    response: `{
  "success": true,
  "data": {
    "driverId": 7,
    "items": [
      {
        "id": 341,
        "platform": "android",
        "deviceModel": "21081111RG",
        "osVersion": "14",
        "appVersion": "3.0.8",
        "ip": "203.0.113.24",
        "loggedInAt": 1792562172317,
        "loggedOutAt": null,
        "logoutReason": null,
        "open": true
      }
    ],
    "page": 1,
    "pageSize": 20,
    "total": 1,
    "openSessions": 1
  }
}`,
  },
  {
    method: 'GET',
    path: '/external/free-trackings/summary',
    title: 'Kilometres per driver',
    summary: 'Sums distance for trips whose start time is inside the window, grouped by driver. Drivers with no trips in the window are omitted.',
    params: [
      ['from', 'Required', 'YYYY-MM-DD or epoch milliseconds. A date starts at 00:00:00.000 UTC.'],
      ['to', 'Required', 'YYYY-MM-DD or epoch milliseconds. A date ends at 23:59:59.999 UTC. The window cannot be longer than 366 days.'],
      ['driverId', 'Optional', 'One driver in this fleet.'],
      ['use', 'Optional', 'all (default), work, or personal.'],
    ],
    request: '/external/free-trackings/summary?from=2026-10-01&to=2026-10-31&use=all',
    response: `{
  "success": true,
  "data": {
    "from": 1759276800000,
    "to": 1761955199999,
    "use": "all",
    "items": [
      {
        "driverId": 7,
        "driverName": "Asha Kumar",
        "tripCount": 12,
        "distanceM": 186400,
        "km": 186.4
      }
    ]
  }
}`,
  },
  {
    method: 'GET',
    path: '/external/live-positions',
    title: 'Last known positions',
    summary: 'The latest point while a trip is still open, otherwise the end of the latest finished trip. An open trip has latitude, longitude, and time, and address is null. A finished trip includes the geocoded address once that fill-in has finished. Drivers with neither an open trip nor a finished trip are omitted.',
    params: [
      ['driverId', 'Optional', 'One driver in this fleet.'],
    ],
    request: '/external/live-positions',
    response: `{
  "success": true,
  "data": {
    "items": [
      {
        "driverId": 7,
        "driverName": "Asha Kumar",
        "lat": 3.139,
        "lng": 101.6869,
        "address": "Jalan Ampang, Kuala Lumpur",
        "recordedAt": 1791697020000
      },
      {
        "driverId": 8,
        "driverName": "Ben Lee",
        "lat": 3.15,
        "lng": 101.7,
        "address": null,
        "recordedAt": 1791698000000
      }
    ]
  }
}`,
  },
  {
    method: 'POST',
    path: '/external/drivers',
    title: 'Create a driver',
    summary: 'Creates a driver in the fleet that owns the key. The password is stored the same way as the portal and is not returned. Username is unique across the whole system.',
    params: [
      ['firstName', 'Required', 'In the JSON body.'],
      ['lastName', 'Required', 'In the JSON body.'],
      ['username', 'Required', 'In the JSON body.'],
      ['password', 'Required', 'At least 8 characters. Not returned.'],
      ['phone', 'Optional', 'In the JSON body.'],
      ['email', 'Optional', 'In the JSON body.'],
      ['freeTrackingEnabled', 'Optional', 'true or false. Defaults to false.'],
      ['freeTrackingAutoStart', 'Optional', 'true or false. Turns Free Tracking on the first time the driver opens the dashboard after each sign-in. Needs freeTrackingEnabled true. Defaults to false.'],
      ['freeTrackingInSettings', 'Optional', 'true or false. Shows the Free Tracking switch in the app Settings screen instead of the dashboard. Defaults to false.'],
    ],
    request: '/external/drivers',
    body: `{
  "firstName": "Asha",
  "lastName": "Kumar",
  "username": "asha",
  "password": "a-long-password",
  "phone": "+60123456789",
  "freeTrackingEnabled": true
}`,
    response: `{
  "success": true,
  "data": {
    "id": 7,
    "firstName": "Asha",
    "lastName": "Kumar",
    "phone": "+60123456789",
    "email": null,
    "licenseNumber": null,
    "licenseExpiry": null,
    "licenseType": null,
    "truckType": null,
    "status": 1,
    "freeTrackingEnabled": true,
    "freeTrackingAutoStart": false,
    "freeTrackingInSettings": false
  }
}`,
  },
  {
    method: 'PATCH',
    path: '/external/drivers/{id}',
    title: 'Change Free Tracking settings',
    summary: 'Same switches as the driver page. Send one or more fields. The phone applies them the next time it opens or comes to the front. Turning Free Tracking off also turns auto start off and sends the tracking.stopped webhook.',
    params: [
      ['id', 'Required', 'Driver id, in the path.'],
      ['freeTrackingEnabled', 'Optional', 'true or false, in the JSON body.'],
      ['freeTrackingAutoStart', 'Optional', 'true or false. Can only be true while Free Tracking is on (422 otherwise).'],
      ['freeTrackingInSettings', 'Optional', 'true or false. Moves the on/off switch to the app Settings screen.'],
    ],
    request: '/external/drivers/7',
    body: `{
  "freeTrackingEnabled": true,
  "freeTrackingAutoStart": true
}`,
    response: `{
  "success": true,
  "data": {
    "driverId": 7,
    "freeTrackingEnabled": true,
    "freeTrackingAutoStart": true,
    "freeTrackingInSettings": false
  }
}`,
  },
  {
    method: 'POST',
    path: '/external/drivers/{id}/notifications',
    title: 'Send a push notification',
    summary: 'Pushes a notification with your title and message to every phone the driver is signed in on (Firebase). The driver must have signed in to the app at least once. data is optional extra key/value pairs delivered with the notification; type and id are reserved.',
    params: [
      ['id', 'Required', 'Driver id, in the path.'],
      ['title', 'Required', 'Up to 100 characters, in the JSON body.'],
      ['message', 'Required', 'Up to 1000 characters, in the JSON body.'],
      ['data', 'Optional', 'Object of up to 10 keys (letters, digits, _), values up to 500 characters.'],
    ],
    request: '/external/drivers/7/notifications',
    body: `{
  "title": "Pickup changed",
  "message": "Please collect from Klinik Ampang before 3pm.",
  "data": { "ref": "REQ-000123" }
}`,
    response: `{
  "success": true,
  "data": {
    "notificationId": 512,
    "driverId": 7,
    "devices": 1,
    "sent": 1,
    "failed": 0
  }
}`,
  },
  {
    method: 'POST',
    path: '/external/drivers/{id}/logout',
    title: 'Force logout',
    summary: 'Revokes every open driver session, the same as Force logout on the driver page. This does not turn Free Tracking off. A phone that stays in the background can keep recording until the app is opened.',
    params: [
      ['id', 'Required', 'Driver id, in the path.'],
    ],
    request: '/external/drivers/7/logout',
    response: `{
  "success": true,
  "data": {
    "driverId": 7,
    "revoked": 1
  }
}`,
  },
  {
    method: 'PATCH',
    path: '/external/drivers/{id}/status',
    title: 'Suspend or reactivate a driver',
    summary: 'suspended matches portal status -1: the driver cannot sign in, open sessions are revoked, and Free Tracking is turned off. The tracking.stopped webhook is sent. active sets status back to 1 and does not turn Free Tracking on again.',
    params: [
      ['id', 'Required', 'Driver id, in the path.'],
      ['status', 'Required', 'suspended or active, in the JSON body.'],
    ],
    request: '/external/drivers/7/status',
    body: `{
  "status": "suspended"
}`,
    response: `{
  "success": true,
  "data": {
    "driverId": 7,
    "status": "suspended",
    "freeTrackingEnabled": false,
    "revoked": 1,
    "sessionsNotRevoked": false
  }
}`,
  },
  {
    method: 'POST',
    path: '/external/webhooks',
    title: 'Register a webhook',
    summary: 'Saves an HTTPS URL for this fleet. At most 5 URLs. The secret is returned once and is used to sign later calls. events defaults to both trip.finished and tracking.stopped.',
    params: [
      ['url', 'Required', 'HTTPS address, in the JSON body.'],
      ['events', 'Optional', 'trip.finished, tracking.stopped, or both.'],
    ],
    request: '/external/webhooks',
    body: `{
  "url": "https://example.com/free-tracking",
  "events": ["trip.finished", "tracking.stopped"]
}`,
    response: `{
  "success": true,
  "data": {
    "id": 3,
    "url": "https://example.com/free-tracking",
    "secret": "hex-secret-shown-once",
    "events": ["trip.finished", "tracking.stopped"]
  }
}`,
  },
  {
    method: 'GET',
    path: '/external/webhooks',
    title: 'List webhooks',
    summary: 'URLs registered for this fleet. The secret is not included.',
    params: [],
    request: '/external/webhooks',
    response: `{
  "success": true,
  "data": {
    "items": [
      {
        "id": 3,
        "url": "https://example.com/free-tracking",
        "events": ["trip.finished", "tracking.stopped"],
        "createdAt": 1791695220000
      }
    ]
  }
}`,
  },
  {
    method: 'DELETE',
    path: '/external/webhooks/{id}',
    title: 'Delete a webhook',
    summary: 'Removes one URL that belongs to this fleet.',
    params: [
      ['id', 'Required', 'Webhook id, in the path.'],
    ],
    request: '/external/webhooks/3',
    response: `{
  "success": true,
  "data": {
    "id": 3,
    "status": "deleted"
  }
}`,
  },
];

const errors = [
  ['200', 'The data object is the result.'],
  ['401', 'The X-Api-Key header is missing, unknown, or revoked.'],
  ['404', 'That trip or driver is not in this fleet.'],
  ['422', 'A required field is missing, a date window is too long, or a parameter is not valid.'],
  ['429', 'This key sent more than 60 requests in one minute.'],
  ['503', 'Free Tracking storage is not ready yet.'],
];
</script>

<template>
  <div class="api-shell">
    <header class="api-header">
      <div class="brand-logo-frame">
        <img :src="headerLogo" alt="iGeoTech" class="brand-logo" />
      </div>
      <div class="brand-sub">Free Tracking API</div>
    </header>
    <main class="api-body">
      <section class="card">
        <h1>Free Tracking API</h1>
        <p>
          Read one fleet’s uploaded Free Tracking trips and drivers, total kilometres, and each driver’s
          last known position. Create a driver, turn Free Tracking on or off, sign them out, or suspend
          the account. Register a webhook for a finished trip or when tracking stops.
          Create a key in the fleet site under Free Tracking, on the same row as Recording settings.
          The full key is shown once.
        </p>
        <p class="label">Base URL</p>
        <code class="url secret-url">{{ API_BASE }}</code>
        <p>Get the base URL from the Igeotech Support Team.</p>
        <p class="label">Postman</p>
        <p>Sample requests for these calls. Set <code>baseUrl</code> to the address from the Igeotech Support Team, and <code>apiKey</code> to a key from Free Tracking.</p>
        <p class="downloads">
          <a href="/free-tracking-external.postman_collection.json" download>Postman collection</a>
          <a href="/free-tracking-external.postman_environment.json" download>Postman environment</a>
        </p>
        <p class="label">Authentication</p>
        <p>Send the key on every data request. There is no login token.</p>
        <pre>X-Api-Key: &lt;your key&gt;</pre>
        <p>
          Times are epoch milliseconds. <code>speed</code> is meters per second.
          <code>heading</code> is degrees clockwise from north, or null when the phone did not report one.
          <code>distanceM</code> and <code>mileageM</code> are meters.
          Driver <code>status</code> is 1 (active), 0 (inactive), or -1 (suspended).
          <code>vehicleType</code> is one of Car, Motorcycle, Van, Pickup, Lorry, Bus, or null.
          <code>useType</code> is work or personal.
          <code>km</code> is kilometres to 3 decimals.
        </p>
      </section>

      <section v-for="endpoint in endpoints" :key="endpoint.method + endpoint.path" class="card">
        <h2>{{ endpoint.title }}</h2>
        <p class="path-line">
          <span class="method">{{ endpoint.method }}</span>
          <code>{{ endpoint.path }}</code>
        </p>
        <p>{{ endpoint.summary }}</p>
        <table v-if="endpoint.params.length">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Required</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in endpoint.params" :key="endpoint.method + row[0]">
              <td><code>{{ row[0] }}</code></td>
              <td>{{ row[1] }}</td>
              <td>{{ row[2] }}</td>
            </tr>
          </tbody>
        </table>
        <p class="label">Example request</p>
        <pre>{{ endpoint.method }} <span class="secret-url">{{ API_BASE }}</span>{{ endpoint.request }}
X-Api-Key: &lt;your key&gt;</pre>
        <pre v-if="endpoint.body">{{ endpoint.body }}</pre>
        <p class="label">Example response</p>
        <pre>{{ endpoint.response }}</pre>
      </section>

      <section class="card">
        <h2>Webhook events</h2>
        <p>
          After a new trip is stored, the server POSTs <code>trip.finished</code> to each registered URL
          that includes that event. It does not wait for the call, and a failed notification does not fail the upload.
          <code>tracking.stopped</code> is sent when a stored trip ends because of logout, disabled, or remote_disabled,
          when this API turns Free Tracking off, or when the account is suspended.
        </p>
        <p>
          The body is JSON. The header <code>X-Free-Tracking-Signature</code> is the hex HMAC-SHA256 of the raw body,
          using the secret returned when the URL was registered. Compare it with the raw bytes you received.
          A failed delivery is tried up to 3 times.
        </p>
        <pre>const crypto = require("crypto");
const expected = crypto.createHmac("sha256", secret).update(rawBody).digest("hex");
const valid = expected === signatureHeader;</pre>
        <p class="label">trip.finished</p>
        <pre>{
  "event": "trip.finished",
  "sentAt": "2026-10-15T06:00:00.000Z",
  "data": {
    "id": 42,
    "driverId": 7,
    "driverName": "Asha Kumar",
    "startedAt": 1791695220000,
    "endedAt": 1791697020000,
    "updatedAt": 1791697100000,
    "distanceM": 32900,
    "movingSeconds": 1400,
    "idleSeconds": 180,
    "stopCount": 2,
    "pointCount": 640,
    "endReason": "manual",
    "useType": "work",
    "vehicleType": "Van",
    "plateNumber": "WXY1234",
    "notes": "Morning delivery"
  }
}</pre>
        <p class="label">tracking.stopped</p>
        <pre>{
  "event": "tracking.stopped",
  "sentAt": "2026-10-15T06:05:00.000Z",
  "data": {
    "driverId": 7,
    "reason": "disabled",
    "freeTripId": null,
    "recordedAt": 1791697500000
  }
}</pre>
        <p>
          <code>reason</code> is logout, disabled, remote_disabled, or suspended.
          <code>freeTripId</code> is the finished trip when the phone ended tracking, and null when this API turned tracking off or suspended the account.
        </p>
      </section>

      <section class="card">
        <h2>Status codes</h2>
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Meaning</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in errors" :key="row[0]">
              <td><code>{{ row[0] }}</code></td>
              <td>{{ row[1] }}</td>
            </tr>
          </tbody>
        </table>
        <p class="label">Error body</p>
        <pre>{
  "success": false,
  "error": {
    "type": "auth",
    "message": "API key is not valid"
  }
}</pre>
      </section>
    </main>
  </div>
</template>

<style scoped>
.api-shell {
  height: 100vh;
  overflow-x: hidden;
  overflow-y: auto;
  background: #f8fafc;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #0f172a;
}
.api-header {
  padding: 16px 22px;
  background: #fff;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.05);
}
.brand-logo-frame {
  width: 130px;
  height: 36px;
  overflow: hidden;
  margin-bottom: 4px;
}
.brand-logo {
  display: block;
  width: 623px;
  height: 90px;
  max-width: none;
  margin: -22px 0 0 -10px;
}
.brand-sub {
  font-size: 11px;
  color: #475569;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}
.api-body {
  max-width: 920px;
  margin: 0 auto;
  padding: 20px;
}
.card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.06);
  padding: 16px 18px;
  margin-bottom: 16px;
}
h1, h2 {
  margin: 0 0 8px;
}
h1 { font-size: 22px; }
h2 { font-size: 18px; }
p { margin: 0 0 10px; font-size: 14px; line-height: 1.5; color: #334155; }
.label {
  margin-top: 12px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}
.url, pre, code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
.url {
  display: block;
  margin-bottom: 10px;
  font-size: 13px;
}
.secret-url {
  filter: blur(6px);
  user-select: none;
}
.downloads {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.downloads a {
  display: inline-block;
  padding: 8px 12px;
  border-radius: 8px;
  background: #e0e7ff;
  color: #3730a3;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
}
.path-line {
  display: flex;
  gap: 8px;
  align-items: center;
}
.method {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: #e0e7ff;
  color: #3730a3;
  font-size: 12px;
  font-weight: 700;
}
pre {
  margin: 0 0 10px;
  padding: 12px;
  overflow: auto;
  border-radius: 8px;
  background: #0f172a;
  color: #e2e8f0;
  font-size: 12px;
  line-height: 1.45;
}
table {
  width: 100%;
  border-collapse: collapse;
  margin: 8px 0 12px;
  font-size: 13px;
}
th, td {
  padding: 8px 10px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: top;
}
th { color: #475569; font-size: 12px; }
</style>
